import { handleActions } from 'redux-actions';

import { actions } from '../../../services/actions';
import { countPlatformNotifications } from '../../../services/mapper/mapAssetsAndDriversWithStates';
import {
    type Address,
    type Asset,
    type CombinedAssetData,
    type Driver,
    type DrivingTimes,
    type GeoBookingState,
    GeoBookingStates,
    type Geofence,
    type Group,
    type PlatformNotification,
    type Pois,
    type TransformedData,
} from '../../../services/types';
import { chargingStationClusterSelected, clusterSelected } from '../../map/mapSlice';
import { sidebarActions } from '../../sidebar/sidebarSlice';

export const defaultDataState = {
    assets: [],
    rawData: null,
    transformedData: [],
    groups: [],
    drivers: [],
    drivingTimes: [],
    addresses: [],
    pois: { workshopPois: [], customerPois: [] },
    geofences: [],
    overallGeoBookingState: GeoBookingStates.inactive,
    fetchInitialDataRequested: false,
    fetchInitialDataFailed: false,
    fetchGroupsRequested: false,
    fetchGroupsFailed: false,
    fetchPoisFailed: false,
    fetchGeofencesRequested: false,
    fetchGeofencesFailed: false,
    fetchAddressesRequested: false,
    fetchAddressesFailed: false,
    activeSidebarElement: null,
};

export type ActiveSidebarElement = {
    id: string;
    type: 'asset' | 'poi' | 'geofence' | 'chargingStation' | 'simplePayPoi';
};

export type DataState = {
    assets: Asset[];
    rawData: CombinedAssetData[] | null;
    transformedData: TransformedData[];
    groups: Group[];
    drivers: Driver[];
    drivingTimes: DrivingTimes[];
    addresses: Address[];
    pois: Pois;
    geofences: Geofence[];
    overallGeoBookingState: GeoBookingState;
    fetchInitialDataRequested: boolean;
    fetchInitialDataFailed: boolean;
    fetchGroupsRequested: boolean;
    fetchGroupsFailed: boolean;
    fetchPoisFailed: boolean;
    fetchGeofencesRequested: boolean;
    fetchGeofencesFailed: boolean;
    fetchAddressesRequested: boolean;
    fetchAddressesFailed: boolean;
    activeSidebarElement: ActiveSidebarElement | null;
};

type ItemWithNotificationCounter = {
    vehicleId: string;
    notificationWarnings?: number;
    notificationExceptions?: number;
};

const updateNotificationData = <T extends ItemWithNotificationCounter>(
    list: T[] | null,
    notificationDict: Record<string, PlatformNotification[]>,
    clearedAssetIds: string[]
) => {
    if (!list) {
        return [];
    }

    // Assets explicitly marked as cleared by the fetch thunk.
    const cleared = new Set(clearedAssetIds);

    return list.map(data => {
        const vehicleNotifications = notificationDict[data.vehicleId];
        if (vehicleNotifications) {
            // Newly received notifications: recalculate counters from payload.
            const notificationsCount = countPlatformNotifications(vehicleNotifications);
            return {
                ...data,
                notificationWarnings: notificationsCount.notificationWarnings,
                notificationExceptions: notificationsCount.notificationExceptions,
            };
        }

        // The fetch layer told us this vehicle was cleared, so reset both counters.
        if (cleared.has(data.vehicleId)) {
            return { ...data, notificationWarnings: 0, notificationExceptions: 0 };
        }

        return data;
    });
};

export const dataReducer = handleActions<DataState, any>(
    {
        [`${actions.assetsChanged}`]: (state: DataState, action) => ({
            ...state,
            assets: action.payload as Asset[],
        }),
        [`${actions.rawDataChanged}`]: (state: DataState, action) => ({
            ...state,
            rawData: action.payload,
            fetchInitialDataFailed: false,
        }),
        [`${actions.platformNotificationsChanged}`]: (state: DataState, action) => ({
            ...state,
            rawData: updateNotificationData(
                state.rawData,
                action.payload.notificationsByAsset,
                action.payload.clearedAssetIds
            ),
            transformedData: updateNotificationData(
                state.transformedData,
                action.payload.notificationsByAsset,
                action.payload.clearedAssetIds
            ),
        }),
        [`${actions.groupsChanged}`]: (state: DataState, action) => ({
            ...state,
            groups: action.payload,
            fetchGroupsFailed: false,
        }),
        [`${actions.driversChanged}`]: (state: DataState, action) => ({ ...state, drivers: action.payload }),
        [`${actions.drivingTimesChanged}`]: (state: DataState, action) => ({ ...state, drivingTimes: action.payload }),
        [`${actions.transformedDataChanged}`]: (state: DataState, action) => ({
            ...state,
            transformedData: action.payload,
        }),
        [`${actions.overallGeoBookingStateChanged}`]: (state: DataState, action) => ({
            ...state,
            overallGeoBookingState: action.payload,
        }),
        [`${actions.activeAssetChanged}`]: (state: DataState, action) => ({
            ...state,
            activeSidebarElement: { type: 'asset', id: action.payload },
        }),
        [`${actions.activePoiChanged}`]: (state: DataState, action) => ({
            ...state,
            activeSidebarElement: { type: 'poi', id: action.payload },
        }),
        [`${actions.activeGeofenceChanged}`]: (state: DataState, action) => ({
            ...state,
            activeSidebarElement: { type: 'geofence', id: action.payload },
        }),
        [`${actions.activeChargingStationChanged}`]: (state: DataState, action) => ({
            ...state,
            activeSidebarElement: { type: 'chargingStation', id: action.payload },
        }),
        [`${actions.activeSimplePayPoiChanged}`]: (state: DataState, action) => ({
            ...state,
            activeSidebarElement: { type: 'simplePayPoi', id: action.payload },
        }),
        [clusterSelected.type]: (state: DataState, action) => {
            if (action.payload) {
                return { ...state, activeSidebarElement: null };
            }
            return state;
        },
        [`${actions.fetchInitialDataRequested}`]: (state: DataState) => ({
            ...state,
            fetchInitialDataRequested: true,
            fetchInitialDataFailed: false,
        }),
        [`${actions.fetchInitialDataFailed}`]: (state: DataState) => ({
            ...state,
            fetchInitialDataFailed: true,
            fetchInitialDataRequested: false,
        }),
        [`${actions.fetchInitialDataSuccess}`]: (state: DataState) => ({
            ...state,
            fetchInitialDataFailed: false,
            fetchInitialDataRequested: false,
        }),

        [`${actions.fetchGroupsRequested}`]: (state: DataState) => ({
            ...state,
            fetchGroupsRequested: true,
            fetchGroupsFailed: false,
        }),
        [`${actions.fetchGroupsFailed}`]: (state: DataState) => ({
            ...state,
            fetchGroupsRequested: false,
            fetchGroupsFailed: true,
        }),
        [`${actions.fetchGroupsSuccess}`]: (state: DataState) => ({
            ...state,
            fetchGroupsRequested: false,
            fetchGroupsFailed: false,
        }),
        [`${actions.fetchPoisFailed}`]: (state: DataState) => ({
            ...state,
            fetchPoisFailed: true,
        }),
        [`${actions.fetchPoisSuccess}`]: (state: DataState, action) => ({
            ...state,
            fetchPoisFailed: false,
            pois: action.payload,
        }),

        [`${actions.fetchGeofencesRequested}`]: (state: DataState) => ({
            ...state,
            fetchGeofencesRequested: true,
            fetchGeofencesFailed: false,
        }),
        [`${actions.fetchGeofencesFailed}`]: (state: DataState) => ({
            ...state,
            fetchGeofencesRequested: false,
            fetchGeofencesFailed: true,
        }),
        [`${actions.fetchGeofencesSuccess}`]: (state: DataState, action) => ({
            ...state,
            fetchGeofencesRequested: false,
            fetchGeofencesFailed: false,
            geofences: action.payload,
        }),

        [`${actions.fetchAddressesRequested}`]: (state: DataState) => ({
            ...state,
            addresses: [],
            fetchAddressesRequested: true,
            fetchAddressesFailed: false,
        }),
        [`${actions.fetchAddressesFailed}`]: (state: DataState) => ({
            ...state,
            fetchAddressesFailed: true,
            fetchAddressesRequested: false,
        }),
        [`${actions.fetchAddressesSuccess}`]: (state: DataState, action) => ({
            ...state,
            addresses: action.payload,
            fetchAddressesFailed: false,
            fetchAddressesRequested: false,
        }),

        [`${sidebarActions.assetSelectedFromClusterSidebar}`]: (state: DataState, action) => ({
            ...state,
            activeSidebarElement: { type: 'asset', id: action.payload },
        }),

        [`${chargingStationClusterSelected}`]: (state: DataState) => ({
            ...state,
            activeSidebarElement: null,
        }),
    },
    defaultDataState
);
